import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import Layout from '@/components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import {
  ArrowLeft,
  Save,
  Loader2,
  BookOpen,
  PlusCircle,
  Trash2,
  Eye,
  Send,
  FileText,
  Users,
  Sparkles,
  GripVertical
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CourseManager = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { isTrainer } = useAuth();
  const [course, setCourse] = useState(null);
  const [modules, setModules] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [showModuleDialog, setShowModuleDialog] = useState(false);
  const [showQuizDialog, setShowQuizDialog] = useState(false);
  const [generatingQuiz, setGeneratingQuiz] = useState(false);
  const [newModule, setNewModule] = useState({
    title: '',
    description: '',
    content: '',
    duration_minutes: 30,
    order: 1
  });
  const [newQuiz, setNewQuiz] = useState({
    title: '',
    description: '',
    questions: [],
    passing_score: 70,
    time_limit_minutes: 15
  });
  const [aiTopic, setAiTopic] = useState('');
  const [aiNumQuestions, setAiNumQuestions] = useState(5);

  useEffect(() => {
    if (!isTrainer) {
      navigate('/dashboard');
      return;
    }
    fetchCourseData();
  }, [courseId, isTrainer]);

  const fetchCourseData = async () => {
    try {
      const [courseRes, modulesRes, quizzesRes, enrollmentsRes] = await Promise.all([
        fetch(`${API}/courses/${courseId}`, { credentials: 'include' }),
        fetch(`${API}/courses/${courseId}/modules`, { credentials: 'include' }),
        fetch(`${API}/quizzes/course/${courseId}`, { credentials: 'include' }),
        fetch(`${API}/courses/${courseId}/enrollments`, { credentials: 'include' })
      ]);

      if (courseRes.ok) setCourse(await courseRes.json());
      if (modulesRes.ok) setModules(await modulesRes.json());
      if (quizzesRes.ok) setQuizzes(await quizzesRes.json());
      if (enrollmentsRes.ok) setEnrollments(await enrollmentsRes.json());
    } catch (error) {
      console.error('Error fetching course data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePublish = async () => {
    setPublishing(true);
    try {
      const res = await fetch(`${API}/courses/${courseId}/publish`, {
        method: 'POST',
        credentials: 'include'
      });

      if (res.ok) {
        setCourse(prev => ({ ...prev, is_published: true }));
        toast.success('Cours publié avec succès !');
      }
    } catch (error) {
      toast.error('Erreur lors de la publication');
    } finally {
      setPublishing(false);
    }
  };

  const handleCreateModule = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/courses/${courseId}/modules`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...newModule,
          order: modules.length + 1
        }),
        credentials: 'include'
      });

      if (res.ok) {
        const module = await res.json();
        setModules([...modules, module]);
        setShowModuleDialog(false);
        setNewModule({
          title: '',
          description: '',
          content: '',
          duration_minutes: 30,
          order: modules.length + 2
        });
        toast.success('Module créé avec succès !');
      }
    } catch (error) {
      toast.error('Erreur lors de la création du module');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteModule = async (moduleId) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer ce module ?')) return;

    try {
      const res = await fetch(`${API}/courses/${courseId}/modules/${moduleId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (res.ok) {
        setModules(modules.filter(m => m.module_id !== moduleId));
        toast.success('Module supprimé');
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleGenerateQuiz = async () => {
    setGeneratingQuiz(true);
    try {
      const res = await fetch(`${API}/ai/generate-quiz`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          topic: aiTopic || course.title,
          num_questions: aiNumQuestions,
          difficulty: course.level
        }),
        credentials: 'include'
      });

      if (res.ok) {
        const data = await res.json();
        setNewQuiz(prev => ({
          ...prev,
          questions: data.questions,
          title: `Quiz - ${aiTopic || course.title}`
        }));
        toast.success('Quiz généré par l\'IA !');
      }
    } catch (error) {
      toast.error('Erreur lors de la génération du quiz');
    } finally {
      setGeneratingQuiz(false);
    }
  };

  const handleCreateQuiz = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/quizzes/course/${courseId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newQuiz),
        credentials: 'include'
      });

      if (res.ok) {
        const quiz = await res.json();
        setQuizzes([...quizzes, quiz]);
        setShowQuizDialog(false);
        setNewQuiz({
          title: '',
          description: '',
          questions: [],
          passing_score: 70,
          time_limit_minutes: 15
        });
        toast.success('Quiz créé avec succès !');
      }
    } catch (error) {
      toast.error('Erreur lors de la création du quiz');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-200 rounded w-1/4" />
          <div className="h-64 bg-slate-200 rounded" />
        </div>
      </Layout>
    );
  }

  if (!course) {
    return (
      <Layout>
        <div className="text-center py-16">
          <h2 className="text-xl font-medium text-slate-900 mb-2">Cours non trouvé</h2>
          <Button onClick={() => navigate('/trainer/courses')}>Retour à mes cours</Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="course-manager">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/trainer/courses')} data-testid="back-btn">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Retour
            </Button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="font-display text-2xl font-bold text-slate-900">
                  {course.title}
                </h1>
                <Badge variant={course.is_published ? 'default' : 'secondary'}>
                  {course.is_published ? 'Publié' : 'Brouillon'}
                </Badge>
              </div>
              <p className="text-slate-600">{course.category}</p>
            </div>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => navigate(`/course/${courseId}`)}
              data-testid="preview-btn"
            >
              <Eye className="h-4 w-4 mr-2" />
              Aperçu
            </Button>
            {!course.is_published && (
              <Button
                onClick={handlePublish}
                disabled={publishing || modules.length === 0}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="publish-btn"
              >
                {publishing ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Send className="h-4 w-4 mr-2" />
                )}
                Publier
              </Button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="modules" className="space-y-6">
          <TabsList>
            <TabsTrigger value="modules">
              <BookOpen className="h-4 w-4 mr-2" />
              Modules ({modules.length})
            </TabsTrigger>
            <TabsTrigger value="quizzes">
              <FileText className="h-4 w-4 mr-2" />
              Quiz ({quizzes.length})
            </TabsTrigger>
            <TabsTrigger value="students">
              <Users className="h-4 w-4 mr-2" />
              Apprenants ({enrollments.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="modules">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Modules du cours</CardTitle>
                  <CardDescription>Organisez le contenu de votre formation</CardDescription>
                </div>
                <Dialog open={showModuleDialog} onOpenChange={setShowModuleDialog}>
                  <DialogTrigger asChild>
                    <Button data-testid="add-module-btn">
                      <PlusCircle className="h-4 w-4 mr-2" />
                      Ajouter un module
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl">
                    <DialogHeader>
                      <DialogTitle>Nouveau module</DialogTitle>
                      <DialogDescription>
                        Créez un nouveau module pour votre cours
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label>Titre</Label>
                        <Input
                          value={newModule.title}
                          onChange={(e) => setNewModule({ ...newModule, title: e.target.value })}
                          placeholder="Titre du module"
                          data-testid="module-title-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Description</Label>
                        <Textarea
                          value={newModule.description}
                          onChange={(e) => setNewModule({ ...newModule, description: e.target.value })}
                          placeholder="Description courte"
                          rows={2}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Contenu</Label>
                        <Textarea
                          value={newModule.content}
                          onChange={(e) => setNewModule({ ...newModule, content: e.target.value })}
                          placeholder="Contenu détaillé du module (Markdown supporté)"
                          rows={8}
                          data-testid="module-content-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Durée (minutes)</Label>
                        <Input
                          type="number"
                          value={newModule.duration_minutes}
                          onChange={(e) => setNewModule({ ...newModule, duration_minutes: parseInt(e.target.value) })}
                          min={5}
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setShowModuleDialog(false)}>
                        Annuler
                      </Button>
                      <Button 
                        onClick={handleCreateModule}
                        disabled={saving || !newModule.title}
                        data-testid="save-module-btn"
                      >
                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Créer'}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                {modules.length === 0 ? (
                  <div className="text-center py-12">
                    <BookOpen className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-600">Aucun module pour le moment</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {modules.sort((a, b) => a.order - b.order).map((module, index) => (
                      <div
                        key={module.module_id}
                        className="flex items-center gap-4 p-4 border rounded-lg"
                        data-testid={`module-item-${module.module_id}`}
                      >
                        <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-sm font-medium">
                          {index + 1}
                        </div>
                        <div className="flex-1">
                          <h4 className="font-medium text-slate-900">{module.title}</h4>
                          <p className="text-sm text-slate-500">{module.duration_minutes} min</p>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteModule(module.module_id)}
                          className="text-red-500 hover:text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="quizzes">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Quiz et évaluations</CardTitle>
                  <CardDescription>Créez des quiz pour évaluer vos apprenants</CardDescription>
                </div>
                <Dialog open={showQuizDialog} onOpenChange={setShowQuizDialog}>
                  <DialogTrigger asChild>
                    <Button data-testid="add-quiz-btn">
                      <PlusCircle className="h-4 w-4 mr-2" />
                      Créer un quiz
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Nouveau quiz</DialogTitle>
                      <DialogDescription>
                        Créez un quiz manuellement ou générez-le avec l'IA
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      {/* AI Generation */}
                      <Card className="bg-teal-50 border-teal-200">
                        <CardContent className="p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <Sparkles className="h-5 w-5 text-teal-600" />
                            <span className="font-medium text-teal-900">Génération par IA</span>
                          </div>
                          <div className="space-y-3">
                            <Input
                              value={aiTopic}
                              onChange={(e) => setAiTopic(e.target.value)}
                              placeholder={`Sujet (par défaut: ${course.title})`}
                              data-testid="ai-topic-input"
                            />
                            <div className="flex gap-3">
                              <Input
                                type="number"
                                value={aiNumQuestions}
                                onChange={(e) => setAiNumQuestions(parseInt(e.target.value))}
                                min={3}
                                max={10}
                                className="w-24"
                              />
                              <Button
                                onClick={handleGenerateQuiz}
                                disabled={generatingQuiz}
                                className="flex-1"
                                data-testid="generate-quiz-btn"
                              >
                                {generatingQuiz ? (
                                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                ) : (
                                  <Sparkles className="h-4 w-4 mr-2" />
                                )}
                                Générer {aiNumQuestions} questions
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      <div className="space-y-2">
                        <Label>Titre du quiz</Label>
                        <Input
                          value={newQuiz.title}
                          onChange={(e) => setNewQuiz({ ...newQuiz, title: e.target.value })}
                          placeholder="Titre du quiz"
                          data-testid="quiz-title-input"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Description</Label>
                        <Textarea
                          value={newQuiz.description}
                          onChange={(e) => setNewQuiz({ ...newQuiz, description: e.target.value })}
                          placeholder="Description du quiz"
                          rows={2}
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Score minimum (%)</Label>
                          <Input
                            type="number"
                            value={newQuiz.passing_score}
                            onChange={(e) => setNewQuiz({ ...newQuiz, passing_score: parseInt(e.target.value) })}
                            min={50}
                            max={100}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Durée (minutes)</Label>
                          <Input
                            type="number"
                            value={newQuiz.time_limit_minutes}
                            onChange={(e) => setNewQuiz({ ...newQuiz, time_limit_minutes: parseInt(e.target.value) })}
                            min={5}
                          />
                        </div>
                      </div>

                      {newQuiz.questions.length > 0 && (
                        <div className="space-y-2">
                          <Label>Questions générées ({newQuiz.questions.length})</Label>
                          <div className="max-h-48 overflow-y-auto space-y-2">
                            {newQuiz.questions.map((q, i) => (
                              <div key={i} className="p-3 bg-slate-50 rounded-lg text-sm">
                                <p className="font-medium">{i + 1}. {q.question}</p>
                                <p className="text-slate-500 text-xs mt-1">
                                  Réponse: {q.correct_answer}
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setShowQuizDialog(false)}>
                        Annuler
                      </Button>
                      <Button
                        onClick={handleCreateQuiz}
                        disabled={saving || !newQuiz.title || newQuiz.questions.length === 0}
                        data-testid="save-quiz-btn"
                      >
                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Créer le quiz'}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                {quizzes.length === 0 ? (
                  <div className="text-center py-12">
                    <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-600">Aucun quiz pour le moment</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {quizzes.map((quiz) => (
                      <div
                        key={quiz.quiz_id}
                        className="flex items-center justify-between p-4 border rounded-lg"
                      >
                        <div>
                          <h4 className="font-medium text-slate-900">{quiz.title}</h4>
                          <p className="text-sm text-slate-500">
                            {quiz.questions?.length || 0} questions - {quiz.time_limit_minutes} min
                          </p>
                        </div>
                        <Badge variant="outline">Score min: {quiz.passing_score}%</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="students">
            <Card>
              <CardHeader>
                <CardTitle>Apprenants inscrits</CardTitle>
                <CardDescription>Suivez la progression de vos apprenants</CardDescription>
              </CardHeader>
              <CardContent>
                {enrollments.length === 0 ? (
                  <div className="text-center py-12">
                    <Users className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-600">Aucun apprenant inscrit</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {enrollments.map((enrollment) => (
                      <div
                        key={enrollment.enrollment_id}
                        className="flex items-center justify-between p-4 border rounded-lg"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                            <span className="text-sm font-medium">
                              {enrollment.user?.name?.charAt(0) || '?'}
                            </span>
                          </div>
                          <div>
                            <h4 className="font-medium text-slate-900">
                              {enrollment.user?.name || 'Utilisateur'}
                            </h4>
                            <p className="text-sm text-slate-500">{enrollment.user?.email}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-medium text-slate-900">
                            {Math.round(enrollment.progress?.progress_percent || 0)}%
                          </p>
                          <p className="text-sm text-slate-500">Progression</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default CourseManager;
